import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../../i18n/index.jsx';
import './ws.css';

// Shared pieces of "Mon espace" and "Équipe" (company users, migration 030).

export const PERM_GROUPS = [
  ['products', ['products.view', 'products.create', 'products.edit', 'products.publish', 'products.delete']],
  ['profile', ['profile.view', 'profile.edit']],
  ['rfq', ['rfq.viewAll', 'rfq.viewAssigned', 'rfq.reply']],
  ['chat', ['chat.own', 'chat.all']],
  ['customers', ['customers.viewAll', 'customers.viewAssigned', 'customers.import', 'customers.export', 'customers.bulkSend']],
  ['emails', ['emails.send']],
  ['whatsapp', ['whatsapp.use']],
  ['docs', ['docs.create', 'docs.send', 'docs.approve', 'docs.templates', 'docs.stamp', 'signatures.manage', 'docs.signOthers', 'docs.signDirect']],
  ['billing', ['billing.view', 'billing.manage']],
  ['users', ['users.manage']],
];
export const ROLES = ['admin', 'manager', 'rep', 'viewer'];
export const CUSTOMER_STATUSES = ['prospect', 'contacted', 'negotiating', 'won', 'lost'];
// "My customers" (migration 035): a crm_customers row is either a
// registered member or a private contact (its own fields, no `users` row).
export const CONTACT_TYPES = ['buyer', 'seller', 'both', 'agent'];
export const SOURCES = ['fair', 'referral', 'website', 'old_client', 'other'];
export const CONSENT_TYPES = ['express', 'implied', 'none'];
export const DOC_KINDS = ['loi', 'rfq', 'quotation', 'sco', 'fco', 'proforma', 'nda', 'letter'];

// Pop-up window. Closes only with ✕, Cancel (the page's own button), Esc,
// or after saving — never by a click outside, a scrollbar drag, a resize or
// zoom (those used to close it at some screen sizes). Rendered in <body>
// (no parent layout can move it); focus and scroll lock are set once, not
// on every re-render of the page behind. Esc closes only the top one.
const openModals = [];
export function Modal({ title, onClose, children, wide, className = '' }) {
  const ref = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const me = {};
    openModals.push(me);
    const prev = document.activeElement;
    ref.current?.querySelector('input:not([type=hidden]), select, textarea, button')?.focus({ preventScroll: true });
    const esc = (e) => { if (e.key === 'Escape' && openModals[openModals.length - 1] === me) { e.stopPropagation(); close.current(); } };
    document.addEventListener('keydown', esc);
    document.body.classList.add('ws-modal-open');
    return () => {
      document.removeEventListener('keydown', esc);
      openModals.splice(openModals.indexOf(me), 1);
      if (!openModals.length) document.body.classList.remove('ws-modal-open');
      if (prev?.isConnected) prev.focus?.({ preventScroll: true });
    };
  }, []);
  return createPortal(
    <div className="ws-backdrop">
      <div ref={ref} className={`ws-modal${wide ? ' is-wide' : ''} ${className}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="ws-modal-head"><h2>{title}</h2><button type="button" className="ws-x" onClick={() => close.current()} aria-label="×">✕</button></div>
        <div className="ws-modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function Avatar({ name, photoUrl, size = 36 }) {
  if (photoUrl) return <img className="ws-avatar" src={photoUrl} alt="" style={{ width: size, height: size }} />;
  const initials = String(name || '?').split(/\s+/).map((x) => x[0]).slice(0, 2).join('').toUpperCase();
  return <span className="ws-avatar ws-avatar-empty" style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden="true">{initials}</span>;
}

export function StatusBadge({ kind, value }) {
  const { t } = useI18n();
  return <span className={`ws-badge ws-${kind}-${value}`}>{t(`ws.${kind}.${value}`)}</span>;
}

export const fmtDate = (d, lang, withTime) => {
  if (!d) return '';
  const x = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00`) : new Date(d);
  return x.toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA', { year: 'numeric', month: 'short', day: 'numeric', ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}) });
};
export const today = () => new Date().toISOString().slice(0, 10);
export const COUNTRIES = ['CA', 'US', 'IN', 'CN', 'MX', 'FR', 'DE', 'GB', 'AE', 'SA', 'TR', 'BR', 'NG', 'ZA', 'PK', 'BD', 'VN', 'ID', 'MA', 'EG'];
export const countryName = (code, lang) => { try { return new Intl.DisplayNames([lang === 'en' ? 'en' : 'fr'], { type: 'region' }).of(code); } catch { return code; } };
export const langName = (code, lang) => { try { return new Intl.DisplayNames([lang === 'en' ? 'en' : 'fr'], { type: 'language' }).of(code); } catch { return code; } };
